import {useSetAtom} from 'jotai';
import {useCallback} from 'react';
import type {Map as MapboxMap} from 'mapbox-gl';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import useSearchParam from 'src/hooks/useSearchParam';
import {placesToFeatureCollection} from './mapUtils';
import {mapLoadingPlaceAtom} from './useMap';
import fetcher from 'src/utils/fetcher';
import useMapUtils from './useMapUtils';

type SetSelectedPlaceOptions = {
  forceFlyTo?: boolean;
};

type Options = {
  places?: Partial<Place>[];
  map: MapboxMap | null;
};

const useMapSelectedPlace = ({places, map}: Options) => {
  const queryClient = useQueryClient();

  const [selectedPlaceQuery, setSelectedPlaceQuery] = useSearchParam('selectedPlace');
  const {flyToPlace} = useMapUtils(map);

  // @ts-ignore Stupid Jotai types
  const setLoadingPlace = useSetAtom(mapLoadingPlaceAtom);

  const listPlace = places?.find((place) => `${place.nameId}_${place.countryCode}` === selectedPlaceQuery);

  const {data} = useQuery<Place>({
    queryKey: ['mapPlace', selectedPlaceQuery],
    queryFn: () =>
      fetcher({
        url: '/api/places/place',
        query: {countryCode: selectedPlaceQuery?.split('_')[1], placeNameId: selectedPlaceQuery?.split('_')[0]}
      }),
    enabled: Boolean(selectedPlaceQuery && !listPlace)
  });

  const setSelectedPlace = useCallback(
    (place: Partial<Place>, {forceFlyTo}: SetSelectedPlaceOptions = {}) => {
      const selectedPlacesSource = map?.getSource('selectedPlaces') as mapboxgl.GeoJSONSource;

      selectedPlacesSource?.setData(placesToFeatureCollection([place]));
      queryClient.setQueryData(['mapPlace', `${place.nameId}_${place.countryCode}`], place);
      setSelectedPlaceQuery(`${place.nameId}_${place.countryCode}`);

      setTimeout(() => {
        flyToPlace(place, {force: forceFlyTo});
      }, 0);
    },
    [map]
  );

  const unSelectPlace = () => {
    const selectedPlacesSource = map?.getSource('selectedPlaces') as mapboxgl.GeoJSONSource;

    selectedPlacesSource?.setData(placesToFeatureCollection([]));
    setSelectedPlaceQuery('');
    setLoadingPlace(null);
  };

  return {place: data || listPlace, setSelectedPlace, unSelectPlace};
};

export default useMapSelectedPlace;
